import { Link } from '@tanstack/react-router'
import { ctaLinkClasses } from '@workspace/ui/components/dsm/cta-button'
import { ArrowRight, Send } from 'lucide-react'
import { useOutreachStatusQuery } from '../hooks/use-outreach-status'
import type { OutreachStatus } from '../types'

interface Props {
  startupId: string
}

interface BannerCopy {
  tone: 'info' | 'success' | 'warn' | 'error'
  title: string
  description: string
  cta: string
}

const STATUS_COPY: Record<OutreachStatus, BannerCopy> = {
  PENDING_SEAT: {
    tone: 'info',
    title: 'Your outreach campaign is on the waitlist',
    description: "We'll open it as soon as a seat is available.",
    cta: 'Open outreach',
  },
  PENDING_PROVISIONING: {
    tone: 'warn',
    title: 'Top up to activate your outreach',
    description:
      'Add credit to your outreach wallet and we’ll provision your campaign automatically.',
    cta: 'Open outreach',
  },
  AWAITING_SENDER: {
    tone: 'info',
    title: 'One-time setup needed for your outreach',
    description: 'Connect your LinkedIn to start sending.',
    cta: 'Finish setup',
  },
  AWAITING_CAMPAIGN: {
    tone: 'info',
    title: "We're preparing your outbound campaign",
    description: 'Setup is wrapping up. This usually takes a few minutes.',
    cta: 'Open outreach',
  },
  READY: {
    tone: 'success',
    title: 'Outreach is live',
    description: 'See the full breakdown: who got contacted, who replied, who booked a call.',
    cta: 'Open outreach dashboard',
  },
  PAUSED: {
    tone: 'warn',
    title: 'Outreach is paused',
    description: 'Resume the campaign and review settings on the outreach page.',
    cta: 'Open outreach',
  },
  PAUSED_DUE_TO_BUDGET: {
    tone: 'warn',
    title: 'Outreach paused: out of balance',
    description: 'Top up your wallet and the campaign picks up where it left off.',
    cta: 'Top up',
  },
  ERROR: {
    tone: 'error',
    title: 'Something went wrong with your outreach campaign',
    description: 'Open the outreach page for details or contact support.',
    cta: 'Open outreach',
  },
  DISCONNECTING: {
    tone: 'warn',
    title: 'Disconnecting your account',
    description: 'Your LinkedIn is being disconnected. This usually takes up to 1 working day.',
    cta: 'Open outreach',
  },
  DISCONNECTED: {
    tone: 'info',
    title: 'Outreach disconnected',
    description: 'Your LinkedIn has been disconnected. You can set up outreach again any time.',
    cta: 'Set up outreach',
  },
}

export function OutreachStatusCard({ startupId }: Props) {
  const { data } = useOutreachStatusQuery(startupId)

  if (!data || data.status === null) return null

  const copy = STATUS_COPY[data.status]
  if (!copy) return null

  return (
    <div className={`mb-6 rounded-xl border px-5 py-4 ${TONE_CLASSES[copy.tone]}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-background/40">
            <Send className="size-4 text-foreground" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{copy.title}</p>
            <p className="text-xs text-muted-foreground">{copy.description}</p>
          </div>
        </div>
        <Link
          to="/app/startup/$id/outreach"
          params={{ id: startupId }}
          className={ctaLinkClasses({ size: 'sm' })}
        >
          <span>{copy.cta}</span>
          <ArrowRight />
        </Link>
      </div>
    </div>
  )
}

const TONE_CLASSES: Record<'info' | 'success' | 'warn' | 'error', string> = {
  info: 'border-primary/30 bg-primary/5',
  success: 'border-emerald-500/30 bg-emerald-500/5',
  warn: 'border-amber-500/30 bg-amber-500/5',
  error: 'border-destructive/30 bg-destructive/5',
}
